import { MaterialTopTabBarProps } from '@react-navigation/material-top-tabs';
import { CommonActions, useFocusEffect } from '@react-navigation/native';
import { createStackNavigator } from '@react-navigation/stack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet } from 'react-native';

import CustomTabBar from '@components/CustomTabBar/CustomTabBar';
import CustomTabPanel from '@components/CustomTabBar/CustomTabPanel.web';
import SecondaryHeader from '@components/Headers/SecondaryHeader';
import CreateJoinTeamModal from '@components/Modals/CreateJoinTeamModal';
import TeamMemberRoleSelectModal from '@components/Modals/TeamMemberRoleSelectModal';
import ProfileCard from '@components/Profile/ProfileCard.web';
import { SubscriptionPlan } from '@components/Profile/SubscriptionPlan.web';
import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { ProfileCardSkeleton } from '@components/shared/skeletons';
import { createTabNavigator } from '@components/Web/Navigator/TabNavigator';
import { Team } from '@graphql/generated';
import { useApolloClient } from '@hooks/useApolloClient';
import { useGoogleAuth } from '@hooks/useGoogleAuth';
import useMe from '@hooks/useMe';
import { useMinimumVisible } from '@hooks/useMinimumVisible';
import { useNotification } from '@hooks/useNotification';
import { useWebDrawerNavigator } from '@hooks/useWebDrawerNavigator';
import { AddTeamMembersStack } from '@navigation/addMembers/add-team-members-stack';
import { modalStyle } from '@navigation/helpers';
import { useAppNavigation } from '@navigation/useAppNavigation';
import { Analytics } from '@screens/Analytics/Analytics';
import { PlanSelect } from '@screens/Payments/PlanSelect';
import { SubscriptionCreatedModal } from '@screens/Payments/SubscriptionCreateModal';
import { MyAccountSettings } from '@screens/Profile/MyAccountSettings.web';
import { AssignNewOwner } from '@screens/Teams/AssignNewOwner';
import TeamMembers from '@screens/Teams/TeamMembers';
import { TeamSubscription } from '@screens/Teams/TeamSubscription';
import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { useAuthContext } from '@src/context/authContext';
import { AccountTeams } from '@src/screens/Profile/AccountTeams.web';
import { useMixpanel } from '@src/utils/mixpanel';
import { AsyncStorage } from '@utils/storage';

const Tabs = createTabNavigator();

// Component to conditionally render tab content only when focused
// This prevents non-active tabs from taking up layout space
const FocusedTabContent: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [isFocused, setIsFocused] = useState(false);

  useFocusEffect(
    React.useCallback(() => {
      setIsFocused(true);
      return () => {
        setIsFocused(false);
      };
    }, [])
  );

  return isFocused ? <>{children}</> : null;
};

type MyTeamsStackParamsList = {
  home: undefined;
  'create-join-team-modal': undefined;
  'team-details': { teamUUId: Team['uuid'] };
  'team-members': { teamUUId: Team['uuid'] };
  'choose-plan': { teamId: Team['id']; teamUUId: Team['uuid'] };
  'team-member-role-select-modal': undefined;
  'team-subscription': { teamUUId: Team['uuid'] };
  'team-analytics': { teamUUId: Team['uuid'] };
  'assign-new-owner': { teamUUId: Team['uuid'] };
  'add-team-members-stack': { teamUUId: Team['uuid'] };
  subscriptionPlanDetail: { teamUUId: Team['uuid'] };
};

export const MyAccount: React.FC<any> = ({ route }) => {
  const { token, removeToken: removeAuthToken } = useAuthContext();
  const { deleteToken: deleteNotificationToken } = useNotification();
  const { trackEvent } = useMixpanel();
  const { me, loading } = useMe();
  const { clearUserInfo } = useGoogleAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  // TA-10280: gate only the ProfileCard slot, not the whole screen — Teams/
  // Subscriptions tabs below have their own loading gates and should mount
  // and skeleton immediately rather than waiting behind `me`.
  const showProfileSkeleton = useMinimumVisible(!me && !!loading);
  const { t } = useTranslation();
  const navigation = useAppNavigation();
  const { client } = useApolloClient();
  const [isFromCreatedSubscription, setIsFromCreatedSubscription] = useState(
    (route?.name || '') === 'subscription-created'
  );
  const [isFromEditSubscription, setFromEditSubscription] = useState(
    (route?.name || '') === 'subscription-edit'
  );
  const { setSelectedTeam } = useWebDrawerNavigator();
  useEffect(() => {
    const unsubscribe = navigation
      .getParent()
      ?.addListener('drawerItemPress', (e) => {
        setSelectedTeam(undefined);
        navigation.reset({ routes: [{ name: 'tabs' }], index: 0 });
        e.preventDefault();
      });
    return unsubscribe;
  }, [navigation]);

  const type = window.sessionStorage.getItem('link_type');
  const id = window.sessionStorage.getItem('task_or_project_id');

  setTimeout(() => {
    if (type === 'project') {
      navigation.navigateToProject({ uuid: id as string, id: '' });
    } else if (type === 'task') {
      navigation.navigateToTask(
        { id: id as string, projectId: '' },
        false,
        true
      );
    }
    window.sessionStorage.setItem('link_type', '');
    window.sessionStorage.setItem('task_or_project_id', '');
  }, 1);

  const logout = async () => {
    if (isLoggingOut) return; // guard against double-clicks
    setIsLoggingOut(true);
    try {
      clearUserInfo && clearUserInfo();
      window.sessionStorage.setItem('screenInfo-activeChat', '');

      if (me?.id) {
        globalThis.localStorage?.removeItem(
          `checklist_onboarding_tooltip_dismissed_${me.id}`
        );
      }

      await deleteNotificationToken();
      await AsyncStorage.clear();
    } catch {
      // empty //
    } finally {
      // Deliberately not resetting isLoggingOut here: this block always
      // completes the logout regardless of earlier failures above, so the
      // button should stay locked until the auth swap unmounts this screen
      // rather than briefly re-enabling and defeating the double-click
      // guard above.
      trackEvent(MIXPANEL_EVENTS.USER_LOGGED_OUT);
      removeAuthToken();
      try {
        await client?.clearStore();
      } catch {
        // empty — best-effort; always still stop the client below.
      } finally {
        client?.stop();
      }
    }
  };

  // Only block the whole screen for the genuine failure case — settled
  // (not loading, not within the skeleton's minimum-visible hold) and still
  // no user. While loading, fall through so Teams/Subscriptions can render.
  if (!me && !showProfileSkeleton && !loading)
    return (
      <Box marginHorizontal='m' marginTop='m'>
        <Text textAlign='center' marginBottom='m' color='grey04'>
          no user found
        </Text>
        <Button
          onPress={logout}
          isLoading={isLoggingOut}
          variant='logout'
          btnTextColor='alertRed'
          accessibilityLabel='Log Out'>
          <Text variant='buttonLabel' color='alertRed'>
            Log out
          </Text>
        </Button>
      </Box>
    );

  const renderTabBar = (props: MaterialTopTabBarProps) => {
    return (
      <Box>
        <Box
          flex={1}
          flexDirection='row'
          alignItems='center'
          style={styles.topShadow}>
          <CustomTabPanel
            // TA-10280 self-review: was keyed on `me?.id`, which is empty
            // while the `me` query is still in flight and only becomes
            // truthy once it resolves — that transition remounted this
            // whole subtree (tearing down Teams/Subscriptions mid-fetch,
            // resetting their skeleton timers) on every ordinary page
            // load. `token` is already stable by the time this screen can
            // even mount (AppStack only renders once `token` is truthy),
            // so it still forces a remount on a genuine account switch
            // (the original TA-4920/5067 intent) without remounting on
            // the loading -> loaded transition of the same session.
            panelKey={'myProfile_' + (token ?? '') + '_' + props.state.index}
            {...props}
            isProfileTabs
            contentContainerStyle={styles.contentContainerPanelStyle}
          />
          <Box flexDirection='row' mr='l' justifyContent='flex-end'>
            <Button
              accessibilityLabel={t('models:saveProfile.logout')}
              isSmall
              height={48}
              prefix={<Icon variant='s' name='LogOut' color='alertRed' />}
              onPress={logout}
              isLoading={isLoggingOut}
              borderRadius='xs'
              variant='logout'
              prefixMarginRight='xs'
              paddingHorizontal='m'
              paddingVertical='xs'>
              {t('models:saveProfile.logout')}
            </Button>
          </Box>
        </Box>
      </Box>
    );
  };

  const Stack1 = createStackNavigator<MyTeamsStackParamsList>();

  const MyProfileStack = () => {
    return (
      <Stack1.Navigator>
        <Stack1.Screen
          name='home'
          children={() => (
            <Box flex={1} marginTop='m'>
              <ScrollView
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps='always'
                scrollEnabled>
                <Box marginBottom='m' marginStart='m' padding='m'>
                  {showProfileSkeleton ? (
                    <ProfileCardSkeleton />
                  ) : (
                    me && <ProfileCard user={me} isMe />
                  )}
                </Box>
                <Box
                  flex={1}
                  width='100%'
                  minHeight={400}
                  marginStart='m'
                  padding='m'>
                  <Tabs.Navigator
                    initialRouteName='Teams'
                    screenOptions={{ swipeEnabled: false, lazy: true }}
                    tabBar={(props) => (
                      <Box pb='s' backgroundColor='white'>
                        <CustomTabBar {...props} />
                      </Box>
                    )}>
                    <Tabs.Screen
                      name='Teams'
                      options={{ tabBarLabel: 'Teams' }}
                      children={() => (
                        <FocusedTabContent>
                          <AccountTeams />
                        </FocusedTabContent>
                      )}
                    />
                    <Tabs.Screen
                      name='subscription-plans'
                      options={{ tabBarLabel: 'Subscriptions' }}
                      children={() => (
                        <FocusedTabContent>
                          <SubscriptionPlan />
                        </FocusedTabContent>
                      )}
                    />
                  </Tabs.Navigator>
                </Box>
              </ScrollView>
            </Box>
          )}
          options={{
            headerShown: false,
          }}
        />
        <Stack1.Screen
          name='team-members'
          children={() => <TeamMembers />}
          options={{
            headerShown: false,
          }}
        />
        <Stack1.Screen
          name='choose-plan'
          children={() => <PlanSelect />}
          options={{
            headerShown: false,
          }}
        />
        <Stack1.Screen
          name='team-analytics'
          children={() => <Analytics />}
          options={{
            headerShown: false,
          }}
        />

        <Stack1.Screen
          name='team-member-role-select-modal'
          children={() => <TeamMemberRoleSelectModal />}
          options={{ ...modalStyle }}
        />
        <Stack1.Screen
          name='create-join-team-modal'
          children={() => <CreateJoinTeamModal />}
          options={{ ...modalStyle }}
        />
        <Stack1.Screen
          name='assign-new-owner'
          children={() => <AssignNewOwner />}
          options={{
            header: () => (
              <SecondaryHeader title='Assign New Owner' searchable={false} />
            ),
          }}
        />
        <Stack1.Screen
          getId={({ params }) => params.teamUUId}
          name='add-team-members-stack'
          children={() => <AddTeamMembersStack isFromMyProfileWeb={true} />}
          options={{ headerShown: false }}
        />
        <Stack1.Screen
          name='team-subscription'
          children={() => <TeamSubscription />}
          options={{
            headerShown: false,
          }}
        />
      </Stack1.Navigator>
    );
  };

  return (
    <Box flex={1} mt='m' minWidth={650}>
      <Tabs.Navigator
        initialRouteName={
          isFromCreatedSubscription ||
          isFromEditSubscription ||
          (route?.name || '') === 'subscription-plans' ||
          window.sessionStorage.getItem('goto_welcome') === '-1'
            ? 'subscription-plans'
            : 'my-profile'
        }
        screenOptions={{ swipeEnabled: false }}
        tabBar={renderTabBar}>
        <Tabs.Screen
          name='my-profile'
          options={{ tabBarLabel: t('models:navigation.myProfile') }}
          children={MyProfileStack}
          listeners={({ navigation }) => ({
            tabPress: (e) => {
              e.preventDefault();
              navigation.dispatch(
                CommonActions.reset({
                  index: 0,
                  routes: [
                    {
                      name: 'my-account',
                      params: {
                        screen: 'tabs',
                      },
                    },
                  ],
                })
              );
            },
          })}
        />
        <Tabs.Screen
          name='settings'
          options={{ tabBarLabel: t('shared:settings') }}
          children={() => <MyAccountSettings />}
        />
      </Tabs.Navigator>
      {isFromCreatedSubscription && (
        <SubscriptionCreatedModal
          isFromCreated={true}
          onClose={() => {
            setIsFromCreatedSubscription(false);
          }}
          title={t('shared:congratulations')}
        />
      )}
      {isFromEditSubscription && (
        <SubscriptionCreatedModal
          isFromCreated={false}
          onClose={() => {
            setFromEditSubscription(false);
          }}
          title={t('shared:congratulations')}
        />
      )}
    </Box>
  );
};

const styles = StyleSheet.create({
  topShadow: {
    backgroundColor: 'white',
  },
  contentContainerPanelStyle: {
    paddingLeft: 24,
    backgroundColor: '#ffffff',
  },
});
